import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n';
import {
  accelerateDeferredOrders,
  fetchDeferredOrders,
} from '../api/orderJobs';

const DEFAULT_HOURS = 12;
const MAX_HOURS = 168;
const REASON_LABELS = {
  balance: 'Недостаточно баланса',
  worker_exception: 'Ошибка обработчика',
  provider_error: 'Ошибка провайдера',
  provider_wait: 'Ожидание провайдера',
  scheduled: 'Отложено системой',
};

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function normalizeHours(value) {
  const hours = Number.parseInt(value, 10);
  if (!Number.isFinite(hours) || hours <= 0) return DEFAULT_HOURS;
  return Math.min(MAX_HOURS, hours);
}

export default function DeferredOrdersPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [inputHours, setInputHours] = useState(DEFAULT_HOURS);
  const [appliedHours, setAppliedHours] = useState(DEFAULT_HOURS);
  const [reasonFilter, setReasonFilter] = useState('all');
  const [reasonSortDirection, setReasonSortDirection] = useState('asc');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accelerating, setAccelerating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const token = user?.api_token;
  const reasonOptions = useMemo(
    () => [...new Set(orders.map((order) => order.defer_reason).filter(Boolean))].sort(),
    [orders],
  );
  const visibleOrders = useMemo(() => {
    const filtered = reasonFilter === 'all'
      ? orders
      : orders.filter((order) => order.defer_reason === reasonFilter);

    return [...filtered].sort((left, right) => {
      const leftReason = t(REASON_LABELS[left.defer_reason] || left.defer_reason || '');
      const rightReason = t(REASON_LABELS[right.defer_reason] || right.defer_reason || '');
      const comparison = leftReason.localeCompare(rightReason);
      return reasonSortDirection === 'asc' ? comparison : -comparison;
    });
  }, [orders, reasonFilter, reasonSortDirection, t]);
  const acceleratableCount = useMemo(
    () => visibleOrders.filter((order) => order.can_accelerate).length,
    [visibleOrders],
  );

  const loadOrders = useCallback(async (hours = appliedHours) => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const payload = await fetchDeferredOrders(token, hours);
      setOrders(Array.isArray(payload.orders) ? payload.orders : []);
      setAppliedHours(payload.hours || hours);
      setInputHours(payload.hours || hours);
    } catch (requestError) {
      setError(requestError.message || t('Ошибка загрузки отложенных заказов'));
    } finally {
      setLoading(false);
    }
  }, [appliedHours, token, t]);

  useEffect(() => {
    loadOrders(DEFAULT_HOURS);
    // Initial load only; later period changes are applied explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const applyPeriod = () => {
    const hours = normalizeHours(inputHours);
    setInputHours(hours);
    loadOrders(hours);
  };

  const accelerate = async () => {
    setAccelerating(true);
    try {
      const result = await accelerateDeferredOrders(token, appliedHours);
      setConfirmOpen(false);
      setSnackbar({
        open: true,
        severity: 'success',
        message: t('Ускорено заказов: {count}').replace('{count}', result.accelerated ?? 0),
      });
      await loadOrders(appliedHours);
    } catch (requestError) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: requestError.message || t('Ошибка ускорения заказов'),
      });
    } finally {
      setAccelerating(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return <Alert severity="error">{t('Доступ запрещён. Только для администратора.')}</Alert>;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('Отложенные заказы')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('Активные заказы с отложенной более чем на 10 минут следующей попыткой')}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <TextField
          label={t('Период, часов')}
          type="number"
          size="small"
          value={inputHours}
          onChange={(event) => setInputHours(event.target.value)}
          inputProps={{ min: 1, max: MAX_HOURS }}
          sx={{ width: 150 }}
        />
        <TextField
          select
          label={t('Причина')}
          size="small"
          value={reasonFilter}
          onChange={(event) => setReasonFilter(event.target.value)}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="all">{t('Все причины')}</MenuItem>
          {reasonOptions.map((reason) => (
            <MenuItem key={reason} value={reason}>
              {t(REASON_LABELS[reason] || reason)}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={applyPeriod}
          disabled={loading || accelerating}
        >
          {t('Показать')}
        </Button>
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          onClick={() => setConfirmOpen(true)}
          disabled={loading || accelerating || acceleratableCount === 0}
        >
          {t('Ускорить обработку')} ({acceleratableCount})
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Alert severity="info" sx={{ mb: 2 }}>
        {t('Найдено заказов за период: {count}')
          .replace('{count}', visibleOrders.length)}
      </Alert>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('Номер заказа')}</TableCell>
              <TableCell>{t('Дата и время оплаты')}</TableCell>
              <TableCell>{t('Сервис')}</TableCell>
              <TableCell>{t('Номер пополнения')}</TableCell>
              <TableCell align="right">{t('Пополняемая сумма')}</TableCell>
              <TableCell sortDirection={reasonSortDirection}>
                <TableSortLabel
                  active
                  direction={reasonSortDirection}
                  onClick={() => setReasonSortDirection((current) => (
                    current === 'asc' ? 'desc' : 'asc'
                  ))}
                >
                  {t('Причина')}
                </TableSortLabel>
              </TableCell>
              <TableCell>{t('Ошибка')}</TableCell>
              <TableCell>{t('Дата создания')}</TableCell>
              <TableCell>{t('Следующая попытка')}</TableCell>
              <TableCell>{t('Состояние')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : visibleOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  {t('Отложенных заказов за выбранный период нет')}
                </TableCell>
              </TableRow>
            ) : visibleOrders.map((order) => (
              <TableRow key={order.job_id} hover>
                <TableCell>
                  <Typography sx={{ fontWeight: 600 }}>
                    {order.external_order_id || `#${order.order_id}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {order.order_id}
                  </Typography>
                </TableCell>
                <TableCell>{formatDate(order.payment_time)}</TableCell>
                <TableCell>{order.service_name || '—'}</TableCell>
                <TableCell>{order.phone_dist || '—'}</TableCell>
                <TableCell align="right">{order.amount_dist ?? '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t(REASON_LABELS[order.defer_reason] || order.defer_reason || '—')}
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 280 }}>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {order.last_error_message || order.last_error_code || '—'}
                  </Typography>
                </TableCell>
                <TableCell>{formatDate(order.created_at)}</TableCell>
                <TableCell>{formatDate(order.next_attempt_at)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={order.can_accelerate ? 'warning' : 'default'}
                    label={t(order.can_accelerate ? 'Можно ускорить' : 'Только просмотр')}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={confirmOpen} onClose={() => !accelerating && setConfirmOpen(false)}>
        <DialogTitle>{t('Ускорить обработку заказов?')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('Ближайшему cron-запуску будут переданы заказы: {count}')
              .replace('{count}', acceleratableCount)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('Будут ускорены только безопасные причины. Статусы и идентификаторы платежей не будут сброшены.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={accelerating}>
            {t('Отмена')}
          </Button>
          <Button variant="contained" onClick={accelerate} disabled={accelerating}>
            {accelerating ? t('Обработка...') : t('Ускорить')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
